"use client";

import { useEffect } from "react";

/**
 * Error boundary raiz do app dir (#237). Antes não existia NENHUM: um chunk
 * antigo respondendo 404 pós-deploy (aba longeva clicando num painel lazy)
 * caía no "Application error" do Next — tela morta, sem recuperação.
 *
 * ChunkLoadError → reload automático UMA vez (guard em sessionStorage contra
 * loop: se o reload não resolver, mostra a UI de erro com botão manual).
 * Nota: global-error renderiza <html>/<body> próprios por contrato do Next
 * (substitui o root layout inteiro), por isso os estilos são inline — o CSS
 * global pode nem ter carregado no momento do erro.
 */

const CHUNK_RELOAD_KEY = "gk-chunk-error-reloaded";

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /loading chunk .* failed|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed/i.test(
      error.message ?? "",
    )
  );
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return; // já tentamos
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    } catch {
      return; // sem storage não há guard anti-loop confiável — não recarrega
    }
    window.location.reload();
  }, [error]);

  return (
    <html lang="pt-BR">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: "#0b1220",
          color: "#e2e8f0",
        }}
      >
        <div style={{ textAlign: "center", padding: 24, maxWidth: 420 }}>
          <h1 style={{ fontSize: 18, marginBottom: 8 }}>
            Algo deu errado ao carregar a página
          </h1>
          <p style={{ fontSize: 14, color: "#94a3b8", marginBottom: 20 }}>
            Pode ser uma atualização recente da plataforma. Recarregar
            normalmente resolve.
          </p>
          <button
            onClick={() => {
              try {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              } catch {
                // segue mesmo sem storage
              }
              window.location.reload();
            }}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#2e86c1",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Recarregar página
          </button>
        </div>
      </body>
    </html>
  );
}
